import type { ColumnConfig, ColumnId, TBAccount } from './types';

/**
 * Default column configuration for Trial Balance Table
 * All visible by default except those explicitly hidden
 * 
 * Sprint 2.9 Update: Increased minWidth for balance columns to 120
 * to accommodate filter icon and financial values
 */
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    id: 'select',
    label: '',
    width: 40,
    minWidth: 40,
    visible: true,
  },
  {
    id: 'accountNumber',
    label: 'Account #',
    width: 100,
    minWidth: 80,
    visible: true,
  },
  {
    id: 'accountName',
    label: 'Account Name',
    width: 250,
    minWidth: 150,
    visible: true,
  },
  {
    id: 'bookBalance',
    label: 'Book Balance',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'jeNumber',
    label: 'JE #',
    width: 80,
    minWidth: 60,
    visible: true,
  },
  {
    id: 'rje',
    label: 'RJE',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'aje',
    label: 'AJE',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'adjBook',
    label: 'Adj Book',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'mAdjNumber',
    label: 'M-Adj #',
    width: 80,
    minWidth: 60,
    visible: true,
  },
  {
    id: 'mAdj',
    label: 'M-Adj',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'ftax',
    label: 'Ftax',
    width: 130,
    minWidth: 120,  // Increased from 100 to accommodate filter icon
    visible: true,
    isCurrency: true,
  },
  {
    id: 'grouping',
    label: 'Grouping',
    width: 150,
    minWidth: 100,
    visible: true,
  },
  {
    id: 'subGrouping',
    label: 'SubGrouping',
    width: 150,
    minWidth: 100,
    visible: true,
  },
  {
    id: 'mAdjRef',
    label: 'M-Adj Ref',
    width: 120,
    minWidth: 80,
    visible: true,
  },
  {
    id: 'pyAdjBook',
    label: 'PY Adj Book',
    width: 130,
    minWidth: 120,  // Increased from 100 to match other balance columns
    visible: true,
    isCurrency: true,
    isReadOnly: true,
  },
  {
    id: 'pyMAdj',
    label: 'PY M-Adj',
    width: 130,
    minWidth: 120,  // Increased from 100 to match other balance columns
    visible: true,
    isCurrency: true,
    isReadOnly: true,
  },
  {
    id: 'pyFtax',
    label: 'PY Ftax',
    width: 130,
    minWidth: 120,  // Increased from 100 to match other balance columns
    visible: true,
    isCurrency: true,
    isReadOnly: true,
  },
];

/**
 * Get value from account for a given column
 */
export function getColumnValue(account: TBAccount, columnId: ColumnId): string | number | null {
  switch (columnId) {
    case 'select':
      return null; // Handled separately
    case 'accountNumber':
      return account.accountNumber;
    case 'accountName':
      return account.accountName;
    case 'bookBalance':
      return account.bookBalance;
    case 'jeNumber':
      return account.jeNumber || '';
    case 'rje':
      return account.rje;
    case 'aje':
      return account.aje;
    case 'adjBook':
      return account.adjBook ?? 0;
    case 'mAdjNumber':
      return account.mAdjNumber || '';
    case 'mAdj':
      return account.mAdj;
    case 'ftax':
      return account.ftax ?? 0;
    case 'grouping':
      return account.grouping;
    case 'subGrouping':
      return account.subGrouping || '';
    case 'mAdjRef':
      return account.mAdjRef || '';
    case 'pyAdjBook':
      return account.pyAdjBook;
    case 'pyMAdj':
      return account.pyMAdj;
    case 'pyFtax':
      return account.pyFtax;
    default:
      return '';
  }
}
